/** @file mesh.setVertexWeights: replaces the influences of one vertex of a skinned mesh. */
import { z } from 'zod';
import { throwError } from '@pose2d/format/throwError';
import { defineCommand } from '../defineCommand';
import { editWeights } from './editWeights';
import { influencePair } from './influencePair';
import { resolveVertexWeights } from './resolveVertexWeights';

/** mesh.setVertexWeights: weights = [[bone, weight], …] for `vertex`, normalized like mesh.setWeights. */
export const command = defineCommand({
  name: 'mesh.setVertexWeights',
  description: "Replace one vertex's influences of a skinned mesh with [[bone, weight], …] (merged, capped at 4, pruned below 0.005, normalized; a newly weighted bone binds at its setup world).",
  input: z.object({ target: z.string().min(1), vertex: z.number().int().nonnegative(), weights: z.array(influencePair) }).strict(),
  apply(draft, input, ctx) {
    editWeights(draft, ctx, input.target, (g, mesh) => {
      const count = mesh.uvs.length / 2;
      if (input.vertex >= count) throwError('VALIDATION', undefined, { path: 'vertex', hint: `the mesh has ${count} vertices (0..${count - 1})` });
      const influences = resolveVertexWeights(ctx, input.weights, 'weights');
      return g.weights.map((v, i) => (i === input.vertex ? influences : v));
    });
  },
});
